import { env } from '@/env';
import { dbg, getOpAccessToken } from '@/lib/logto';

/**
 * Collegamento di un'identita' social all'account QLASH **gia' esistente**, via
 * Account API di Logto (`/api/verifications/social` + `/api/my-account/identities`).
 *
 * ⚠️ Non e' il login social. Il login con Telegram crea un'identita' NUOVA (e la
 * sign-up policy di staging pretende un'email verificata che Telegram non porta):
 * e' la strada che si e' fermata cinque volte su cinque. Questa invece parte da una
 * sessione autenticata e AGGIUNGE l'identita' all'utente che sta guardando lo schermo.
 *
 * Catena, e cosa e' misurato di ciascun anello [M 2026-09-08, staging, seggio
 * tma-account-orc salvo dove detto]:
 *  1. `POST /api/verifications/social {connectorId, redirectUri, state}` → **201**
 *     con `verificationRecordId` + `authorizationUri` + `expiresAt` (~10 min).
 *     ✅ Misurato **con il connector NON esposto** nella sign-in experience (4 target:
 *     discord, google, apple, twitch) ⇒ **esporlo non serve**: l'Account API lo
 *     indirizza per `connectorId`. Controllo negativo: un `connectorId` inventato
 *     torna **422 `session.invalid_connector_id`**, quindi il 201 non e' un «si' a tutto».
 *  2. L'utente autorizza su `oauth.telegram.org`. ✅ Misurato che il nostro `state`
 *     viaggia **dentro** l'`authorizationUri`, quindi torna nel callback ed e'
 *     confrontabile.
 *  3. `POST /api/verifications/social/verify {connectorId, verificationRecordId,
 *     connectorData}` con i parametri del callback.
 *  4. `POST /api/my-account/identities` con l'header `logto-verification-id`.
 *     ⚠️ **[T da or50, non verificato da me]**: il nome dell'header e la forma del
 *     corpo vengono dal suo giro del 07/09. Non e' verificabile senza attaccare
 *     davvero un'identita' a un utente vero, che richiede un umano su Telegram.
 *
 * 🔴 **Il vincolo che decide se questo codice puo' girare, e non e' nostro**: Telegram
 * accetta il `redirect_uri` **solo sui domini registrati nel client OAuth in BotFather**.
 * Oggi c'e' solo `stg-account.qlash.gg`: un ritorno su `stg-tma.qlash.gg` riceve
 * `redirect_uri required` (21 byte, **identico** al controllo negativo `example.com`),
 * mentre un path inventato su `stg-account.qlash.gg` riceve la pagina vera (6928 byte)
 * ⇒ **valida il DOMINIO, non l'URL**. Finche' il dominio della TMA non e' registrato
 * (atto di Luca in BotFather, voce L-695), il passo 2 si ferma da Telegram e nessun
 * codice di qui puo' cambiarlo.
 */

export const PENDING_SOCIAL_LINK = 'qlash.pending_social_link';

export interface PendingSocialLink {
  target: string;
  connectorId: string;
  verificationRecordId: string;
  state: string;
  redirectUri: string;
}

export type CompleteOutcome = 'linked' | 'no-pending' | 'state-mismatch' | 'failed';

/** I connector per target. Vuoto = il collegamento non e' disponibile su questa build. */
const CONNECTOR_BY_TARGET: Readonly<Record<string, string>> = {
  telegram: env.logtoTelegramConnectorId,
};

/**
 * Vero solo se questa build sa a quale connector parlare.
 *
 * 🔒 E' il punto in cui la riserva «Account Center in produzione = GO a parte» smette
 * di essere una promessa e diventa un fatto della build: `.env.production` non
 * definisce `VITE_LOGTO_TELEGRAM_CONNECTOR_ID`, il default e' la stringa vuota, e il
 * bottone non si disegna. Non un flag da ricordare di spegnere: un valore che non c'e'.
 */
export function canLinkSocial(target: string): boolean {
  return env.logtoAccountApiEnabled && !!CONNECTOR_BY_TARGET[target];
}

/**
 * 🔴 SCOLLEGARE NON HA GLI STESSI PREREQUISITI DI COLLEGARE, e legarli e' stato un
 * difetto trovato da Luca guardando lo schermo: *«non posso scollegare account da
 * discord o google (quello dovrebbe essere piu' facile da tma?)»*.
 * **Collegare** deve sapere **a quale connector parlare** (serve il suo id, e su questa
 * build ce l'ha solo Telegram). **Scollegare no**: la `DELETE` prende il **target** nel
 * path e non chiede nessuna verifica — basta essere l'utente. ⇒ vale per **ogni**
 * identita' che l'account ha, connector configurato o no.
 * ⛔ Resta il solo prerequisito vero: che **l'Account API sia accesa** su questo tenant.
 */
export function canUnlinkSocial(): boolean {
  return env.logtoAccountApiEnabled;
}

/**
 * 🔴 IL RITORNO NON PUO' ESSERE UNA NOSTRA ROTTA, e la ragione non e' nostra.
 *
 * Telegram accetta il `redirect_uri` **solo sul dominio registrato per il bot**, e in
 * BotFather quel dominio e' **UNO SOLO** — `Bot Settings → Domain`, con i pulsanti
 * `Remove domain` / `Edit domain`, verbi al singolare [M dal MacMini, 2026-09-08 23:0xZ].
 * Oggi vale `stg-account.qlash.gg`, **cioe' Logto**, ed e' cio' di cui vive il LOGIN
 * Telegram. ⇒ ***il dominio unico e' un aut-aut fra login e collegamento***: puntarlo alla
 * TMA avrebbe spento il login **in silenzio**, perche' `Edit domain` sostituisce.
 *
 * ⇒ Si usa il **callback del connector**, che sta gia' su quel dominio: nessun atto in
 * BotFather, nessun rischio per il login.
 * [M 2026-09-08 23:0xZ] `POST /api/verifications/social` con questo `redirectUri` → **201**,
 * e l'`authorizationUri` lo riporta pari pari; e quel dominio Telegram lo accetta gia'
 * (pagina vera, 6977 byte, contro i **21** — `redirect_uri required` — di `stg-tma.qlash.gg`,
 * identici al controllo negativo `example.com`).
 *
 * ⚠️ **COSA QUEL 201 NON PROVA, ed e' la domanda aperta di questo file**: che il giro si
 * CHIUDA. Se l'utente atterra sul callback di *Logto*, il codice lo prende Logto e **non e'
 * detto che torni a noi**. Non e' deducibile da qui: si vede solo con un tocco umano, e la
 * prova e' server-side — `GET /api/my-account` deve mostrare `identities.telegram`.
 * ⛔ **Per questo NON e' stata costruita nessuna logica di completamento su quel ramo**:
 * sarebbe codice appoggiato a un meccanismo che nessuno ha ancora visto funzionare.
 * `completeSocialLink` e la rotta `/auth/link-callback` restano e servono **se** Logto ci
 * rimanda i parametri — non si perde niente, e non si finge di sapere.
 */
export function getLinkRedirectUri(): string {
  const connectorId = CONNECTOR_BY_TARGET.telegram;
  return new URL(`callback/${connectorId}`, env.logtoEndpoint).toString();
}

export function readPendingSocialLink(): PendingSocialLink | null {
  try {
    const raw = localStorage.getItem(PENDING_SOCIAL_LINK);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<PendingSocialLink>;
    if (!p.connectorId || !p.verificationRecordId || !p.state) return null;
    return p as PendingSocialLink;
  } catch {
    return null;
  }
}

export function clearPendingSocialLink(): void {
  localStorage.removeItem(PENDING_SOCIAL_LINK);
}

function newState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function accountApiUrl(path: string): string {
  return new URL(path, env.logtoEndpoint).toString();
}

/**
 * Apre un giro di collegamento: chiede a Logto il record di verifica e restituisce
 * l'URI da aprire. **Il record in attesa si scrive PRIMA di restituire**, perche' il
 * callback puo' atterrare su un caricamento di pagina nuovo (il ponte del browser di
 * sistema): un record scritto dal chiamante *dopo* lascerebbe, a ogni chiusura della
 * app fra i due passi, un giro che nessuno puo' chiudere e che non ha sintomi.
 */
export async function startSocialLink(
  target: 'telegram',
): Promise<{ authorizationUri: string } | null> {
  const connectorId = CONNECTOR_BY_TARGET[target];
  if (!connectorId) {
    dbg(`link ${target}: nessun connector configurato su questa build`);
    return null;
  }

  const token = await getOpAccessToken();
  if (!token) {
    dbg(`link ${target}: nessun token OP`);
    return null;
  }

  const state = newState();
  const redirectUri = getLinkRedirectUri();

  let res: Response;
  try {
    res = await fetch(accountApiUrl('api/verifications/social'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ connectorId, redirectUri, state }),
    });
  } catch (err) {
    dbg(`link ${target}: rete caduta sulla creazione del record (${String(err)})`);
    return null;
  }

  if (!res.ok) {
    // 400 `account_center.not_enabled` = l'Account API e' spenta sul tenant. E' una
    // configurazione, non un difetto di questo codice: distinguerla nel registro
    // evita di cercare il guasto qui dentro.
    dbg(`link ${target}: creazione record fallita, HTTP ${res.status}`);
    return null;
  }

  const data = (await res.json()) as { verificationRecordId?: string; authorizationUri?: string };
  if (!data.verificationRecordId || !data.authorizationUri) {
    dbg(`link ${target}: risposta 2xx senza i campi attesi`);
    return null;
  }

  localStorage.setItem(
    PENDING_SOCIAL_LINK,
    JSON.stringify({
      target,
      connectorId,
      verificationRecordId: data.verificationRecordId,
      state,
      redirectUri,
    } satisfies PendingSocialLink),
  );

  return { authorizationUri: data.authorizationUri };
}

/**
 * Chiude il giro con i parametri del callback.
 *
 * ⚠️ Lo `state` si confronta **prima** di qualunque chiamata: la rotta di callback e'
 * pubblica e chiunque conosca l'URL puo' aprirla con dei parametri propri. La garanzia
 * che conta non e' che torni un errore, e' che la chiamata che **attacca l'identita'**
 * non parta. E su disaccordo il record NON si consuma: un callback legittimo in
 * ritardo deve poter ancora chiudere il giro.
 */
export async function completeSocialLink(
  params: Record<string, string>,
): Promise<CompleteOutcome> {
  const pending = readPendingSocialLink();
  if (!pending) return 'no-pending';
  if (!params.state || params.state !== pending.state) {
    dbg('link: state del callback diverso dal nostro — mi fermo prima di attaccare');
    return 'state-mismatch';
  }

  const token = await getOpAccessToken();
  if (!token) return 'failed';
  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  try {
    const verify = await fetch(accountApiUrl('api/verifications/social/verify'), {
      method: 'POST',
      headers: auth,
      body: JSON.stringify({
        connectorId: pending.connectorId,
        verificationRecordId: pending.verificationRecordId,
        connectorData: params,
      }),
    });
    if (!verify.ok) {
      dbg(`link: verify fallita, HTTP ${verify.status}`);
      return 'failed';
    }

    const attach = await fetch(accountApiUrl('api/my-account/identities'), {
      method: 'POST',
      headers: { ...auth, 'logto-verification-id': pending.verificationRecordId },
      body: JSON.stringify({}),
    });
    if (!attach.ok) {
      dbg(`link: identities fallita, HTTP ${attach.status}`);
      return 'failed';
    }
  } catch (err) {
    dbg(`link: rete caduta durante la chiusura (${String(err)})`);
    return 'failed';
  }

  clearPendingSocialLink();
  return 'linked';
}

/**
 * Toglie un'identità social dall'account (Account API, `DELETE /api/my-account/identities/:target`).
 *
 * 🔴 **È un atto di segno opposto al collegamento, e non è la sua simmetria tecnica**: collegare
 * chiede una verifica (l'utente deve dimostrare di possedere quell'account); **scollegare no** —
 * basta essere l'utente. Per questo non passa da nessun record di verifica, e per questo la
 * schermata deve chiedere conferma: **l'unico attrito che esiste è quello che ci mettiamo noi.**
 *
 * ⚠️ Il target sta nel **path**. Sbagliarlo non produce un errore: scollega **un'altra identità**
 * e risponde `204`. È un guasto che **riesce**, ed è la ragione per cui il test asserisce sull'URL
 * e non solo sull'esito.
 */
export async function unlinkSocial(target: string): Promise<boolean> {
  const token = await getOpAccessToken();
  if (!token) {
    dbg(`unlink ${target}: nessun token OP`);
    return false;
  }
  try {
    const res = await fetch(accountApiUrl(`api/my-account/identities/${target}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      dbg(`unlink ${target}: HTTP ${res.status}`);
      return false;
    }
    return true;
  } catch (err) {
    dbg(`unlink ${target}: rete caduta (${String(err)})`);
    return false;
  }
}
